import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../../../../components/recharts/functions";
import { ReMixChart } from "../../../../../../components/recharts/ReMixCharts";
import { BrushStart, ChartType } from "../../../../../../models/chart_model";
import { renderHourTick } from "../../../../../../components/recharts/components";
import { R_CHART_TYPES } from "../../../../const";
import { fetchOptimalProcurementcummulativeData } from "../../../../../../Rest_api/DemoRestApi";
import loader from "../../../../../../components/recharts/loader.gif";
import { getMonthFromDate } from "../../../../../../extensions/date";

const DataTabs = ["Slot", "Date", "Month"];

interface CumulativeData {
  date: string;
  slot: number;
  cummulated_vol: number;
  cummulated_cost: number;
  month?: string;
  [key: string]: any;
}

const Intervals = [
  {
    label: "Time Slot",
    key: "time",
    values: ["slot", "date"],
    isTime: true,
  },
  {
    label: "Date",
    key: "date",
    values: ["date"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month",
    values: ["month"],
    isTime: false,
  },
];

export default function OptimalCumulatedVolumeAnalysis({
  selectedBlock,
  refreshKey,
  setError,
  setUniqueTargetDate,
  selectedTargetDate,
}: {
  selectedBlock: any[];
  refreshKey: number;
  setError?: any;
  setUniqueTargetDate?: any;
  selectedTargetDate: any[];
}) {
  const [data, setData] = useState<CumulativeData[]>([]);
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<string>(DataTabs[0]);
  const [selectedInterval, setSelectedInterval] = useState(Intervals[0]);

  useEffect(() => {
    const tabIndex = DataTabs.indexOf(activeTab);
    if (tabIndex !== -1) {
      setSelectedInterval(Intervals[tabIndex]);
    }
  }, [activeTab]);

  const handleFetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetchOptimalProcurementcummulativeData();
      const res = response;
      let dataArray = Array.isArray(res) ? res : res?.data || [];

      if (!Array.isArray(dataArray)) {
        console.error("Data is not an array:", dataArray);
        setError?.("Invalid data format: expected array.");
        setData([]);
        return;
      }

      // Clean and validate data
      const cleanedData = dataArray.map((item) => ({
        ...item,
        cummulated_vol: Number(item.cummulated_vol) || 0,
        cummulated_cost: Number(item.cummulated_cost) || 0,
        slot: Number(item.slot) || 0,
        month: getMonthFromDate(item.date),
      }));

      if (setUniqueTargetDate && cleanedData.length > 0) {
        const uniqueDates = Array.from(
          new Set(
            cleanedData
              .filter((item) => item.date)
              .map((item: { date: string }) =>
                item.date.split("-").reverse().join("-")
              )
          )
        );
        setUniqueTargetDate(uniqueDates);
      }

      setData(cleanedData);
    } catch (error) {
      console.error("Error fetching data", error);
      setError?.("An error occurred while fetching data.");
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [setError, setUniqueTargetDate]);

  useEffect(() => {
    handleFetchData();
  }, [handleFetchData, refreshKey]);

  const handlexDataKey = useCallback(() => {
    if (selectedInterval.key === "time") {
      return "slot";
    }
    return selectedInterval.key;
  }, [selectedInterval]);

  const xDataKey = useMemo(() => handlexDataKey(), [handlexDataKey]);

  useEffect(() => {
    if (!data || data.length === 0) {
      setChartData([]);
      setChartKeys([]);
      return;
    }

    let filteredData = [...data];

    // Filter by selected dates if any
    if (selectedTargetDate && selectedTargetDate.length > 0) {
      const types = selectedTargetDate.map(
        (t: any) => t.value?.split("-").reverse().join("-") ?? t
      );
      filteredData = filteredData.filter((row) => types.includes(row.date));
    }

    function getKeys() {
      if (selectedInterval.key === "time") {
        return ["slot", "date"];
      } else if (selectedInterval.key === "date") {
        return ["date"];
      }
      return [selectedInterval.values[0]];
    }

    const aggregationKeys = ["cummulated_vol"];

    const par: ChartDataParams = {
      keys: getKeys(),
      tableKeys: ["slot", "date", "month"],
      groupBy: [""],
      volumeKey: "",
      aggregationKeys: aggregationKeys,
      averageKeys: [],
      averageDuplicationKeys: [],
      priceKey: "",
      data: filteredData,
    };

    const cData = FormatChartData(par);

    // Additional cleaning after aggregation
    const cleanedChartData = cData.data.map((item: any) => {
      const cleaned = { ...item };
      Object.keys(cleaned).forEach((key) => {
        const value = cleaned[key];
        if (typeof value === "number" && (isNaN(value) || !isFinite(value))) {
          cleaned[key] = 0;
        } else if (
          typeof value === "string" &&
          (value === "NaN" || value.toLowerCase() === "nan")
        ) {
          cleaned[key] = 0;
        }
      });
      return cleaned;
    });
    setChartData(cleanedChartData);
    setChartKeys(cData.keys);
  }, [data, selectedBlock, selectedInterval, selectedTargetDate]);

  const createChartObject = useCallback(
    (
      key: string,
      suffix: string,
      displayName: string,
      chartType: ChartType,
      unit: string,
      stroke: string,
      fill: string,
      yAxisId: string
    ) => {
      const dataKey = key ? `${key}_${suffix}` : suffix;
      return {
        dataKey: dataKey,
        name: displayName,
        legendColor: fill === "none" ? stroke : fill,
        type: chartType,
        unit: unit,
        stroke: stroke,
        fill: fill,
        yAxisId: yAxisId,
        fillOpacity: fill !== "none" ? 0.6 : 0, // Add opacity for area fill
      };
    },
    []
  );

  const legends = useMemo(() => {
    if (!chartKeys || chartKeys.length === 0) {
      return [];
    }

    const metrics = [
      {
        suffix: "cummulated_vol",
        displayName: "Cumulated Net Surplus/Deficit",
        chartType: ChartType.Area, // Changed from Line to Area
        unit: "MWh",
        stroke: "#F1935CCC", // Orange color matching the image
        fill: "#F1935CCC", // Use gradient fill
        yAxisId: "left",
      },
    ];

    const result = metrics.flatMap(
      ({ suffix, displayName, chartType, unit, yAxisId, stroke, fill }) =>
        chartKeys.map((key) =>
          createChartObject(
            key,
            suffix,
            displayName,
            chartType,
            unit,
            stroke,
            fill,
            yAxisId
          )
        )
    );

    return result;
  }, [chartKeys, createChartObject]);

  function secondDataKey() {
    if (selectedInterval.isTime && selectedBlock.length > 0) {
      return "date";
    }
    if (selectedInterval.isTime) {
      return "date";
    }
    return undefined;
  }

  return (
    <div className="dash-card !border !border-[#DFEAF2] bg-white shadow-[rgba(149,157,165,0.2)_0px_8px_24px] w-full !min-h-[100%] rounded-2xl p-4">
      <div className="flex justify-between items-center border-b border-[#DFEAF2] pb-3 mb-4">
        <div className="flex items-center gap-3">
          <h2 className="chartHeading !text-[#34656D]">
            Cumulated Net System Balance (Surplus (+)/ Deficit (-))
          </h2>
        </div>
      </div>

      {loading ? (
        <div className="w-full bg-white flex items-center justify-center min-h-[400px]">
          <img src={loader} width={200} alt="loader" className="block" />
        </div>
      ) : chartData.length === 0 ? (
        <div className="w-full bg-white flex items-center justify-center min-h-[400px]">
          <p className="text-gray-500">No data available</p>
        </div>
      ) : (
        <>
          {/* Add gradient definition for the area fill */}
          <svg width="0" height="0">
            <defs>
              <linearGradient id="colorCost" x1="0" y1="0" x2="0" y2="1">
                <stop offset="80%" stopColor="#F1935CCC" stopOpacity={0.15} />
                <stop offset="0%" stopColor="#F1935C00" stopOpacity={0.85} />
              </linearGradient>
            </defs>
          </svg>

          <ReMixChart
            chartHeight="calc(100% - 70px)"
            data={chartData}
            showBrush={chartData.length > 96 ? true : undefined}
            brushStart={BrushStart.Start}
            isTimeSlot={selectedInterval.isTime}
            onlyTitle={true}
            legends={legends}
            yAxisLabel="MWh"
            secondXAxisLabel=""
            nameGap={40}
            xDataKey={xDataKey}
            top={10}
            bottom={30}
            xTick={
              selectedInterval.isTime && selectedBlock.length === 0
                ? (value: any, index: any) => renderHourTick(value, 10)
                : undefined
            }
            secondXDataKey={secondDataKey()}
            showToolbox={false}
          />
        </>
      )}
    </div>
  );
}
